/**
 * Base local de aeroportos — a camada que responde ANTES da rede.
 *
 * Lógica pura + um carregador. Sem React, sem estado de componente.
 *
 * POR QUE ISTO EXISTE
 * ───────────────────
 * O autocomplete do vendedor ia à Moblix a cada tecla, inclusive para GRU,
 * SSA e VDC — os códigos que a agência mais digita. O sistema já tinha
 * esses 52 aeroportos com cidade e nome dentro do próprio repositório
 * (`backend/src/domain/rules/airport_rules.py`), publicados agora em
 * `GET /api/public/airports/all`.
 *
 * A REGRA QUE GOVERNA ESTE MÓDULO
 * ───────────────────────────────
 * **O resultado local nunca espera a Moblix.** A Moblix complementa o
 * dropdown; ela não é dona dele.
 *
 * Isso resolve o dilema entre velocidade e cobertura sem escolher um lado:
 *
 *   - a base local tem 52 aeroportos e o catálogo real é maior. BPS
 *     (Porto Seguro), CUN, MEX, BOG e LIM NÃO estão aqui;
 *   - se o local "resolvesse e parasse", digitar POR mostraria Porto,
 *     Porto Alegre e Porto Velho — e Porto Seguro sumiria. Para um
 *     vendedor isso não se lê como "cobertura parcial do dataset local",
 *     se lê como "Porto Seguro não existe no sistema";
 *   - então: pinta o local na hora, consulta a Moblix em paralelo, e
 *     mescla. A cobertura nunca fica pior do que era; a espera, sim.
 *
 * A única busca que NÃO chama a Moblix é o código IATA de 3 letras que já
 * existe na base local (GRU, SSA, VDC...): ali não há o que complementar,
 * o aeroporto é exatamente aquele.
 */

const LIMITE_PADRAO = 8;
const ROTA_BASE_LOCAL = '/public/airports/all';

/**
 * Palavras que aparecem em quase todo nome de aeroporto e não distinguem
 * nada. Sem esta lista, digitar "AER" casaria com os 52 (todos têm
 * "Aeroporto" no nome) e o dropdown viraria ruído.
 */
const PALAVRAS_IGNORADAS = new Set([
  'aeroporto', 'aeroportos', 'internacional', 'internacionais',
  'de', 'do', 'da', 'dos', 'das', 'e',
]);

/** Minúsculas, sem acento, sem espaço nas pontas. "Vitória" → "vitoria". */
export function normalizar(texto) {
  return String(texto == null ? '' : texto)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/** Tem a FORMA de um código IATA. Não diz se o aeroporto existe. */
export function pareceCodigoIata(termo) {
  return /^[a-z]{3}$/.test(normalizar(termo));
}

/** Palavras úteis de um texto, já normalizadas e sem as genéricas. */
function palavrasUteis(texto) {
  return normalizar(texto)
    .split(/[^a-z0-9]+/)
    .filter((p) => p && !PALAVRAS_IGNORADAS.has(p));
}

/**
 * Quão bem um aeroporto responde ao termo. 0 = não responde (fica de fora).
 *
 * A escala é grosseira de propósito: o que importa é a ORDEM, e ela precisa
 * caber na cabeça de quem for ler isto depois.
 *
 *   4  código exato          "GRU" → GRU
 *   3  cidade começa com     "por" → Porto, Porto Alegre, Porto Velho
 *   2  palavra da cidade     "con" → Vitória da Conquista
 *   1  palavra do nome       "gua" → Guarulhos
 *   0.5 código começa com    "gr"  → GRU, GRC
 */
export function pontuar(aeroporto, termo) {
  const alvo = normalizar(termo);
  if (!alvo) return 0;

  const code = normalizar(aeroporto && aeroporto.code);
  const city = normalizar(aeroporto && aeroporto.city);

  if (code && code === alvo) return 4;
  if (city && city.startsWith(alvo)) return 3;
  if (palavrasUteis(aeroporto && aeroporto.city).some((p) => p.startsWith(alvo))) return 2;
  if (palavrasUteis(aeroporto && aeroporto.name).some((p) => p.startsWith(alvo))) return 1;
  if (code && code.startsWith(alvo)) return 0.5;
  return 0;
}

/**
 * Filtra a base local. Puro, síncrono, sem rede — é isto que roda entre a
 * tecla e o dropdown.
 */
export function buscarLocal(base, termo, limite = LIMITE_PADRAO) {
  const alvo = normalizar(termo);
  if (!Array.isArray(base) || alvo.length < 2) return [];

  return base
    .map((ap) => ({ ap, nota: pontuar(ap, alvo) }))
    .filter((x) => x.nota > 0)
    .sort((a, b) => {
      if (b.nota !== a.nota) return b.nota - a.nota;
      const cidade = String(a.ap.city || '').localeCompare(String(b.ap.city || ''), 'pt-BR');
      if (cidade !== 0) return cidade;
      return String(a.ap.code || '').localeCompare(String(b.ap.code || ''));
    })
    .slice(0, limite)
    .map((x) => x.ap);
}

/**
 * Junta o que veio do local com o que veio da Moblix.
 *
 * Local primeiro (já está na tela; reordenar embaixo do dedo do vendedor
 * seria pior que esperar), remoto depois, sem repetir código.
 */
export function mesclar(locais, remotos, limite = LIMITE_PADRAO) {
  const vistos = new Set();
  const saida = [];

  for (const ap of [...(locais || []), ...(remotos || [])]) {
    const code = String((ap && ap.code) || '').toUpperCase();
    if (!code || vistos.has(code)) continue;
    vistos.add(code);
    saida.push(ap);
    if (saida.length >= limite) break;
  }

  return saida;
}

/** Aeroporto da base local com o código exato, ou null. */
export function acharPorCodigo(base, codigo) {
  const alvo = String(codigo || '').toUpperCase().trim();
  if (!Array.isArray(base) || alvo.length !== 3) return null;
  return base.find((ap) => String(ap.code || '').toUpperCase() === alvo) || null;
}

// ─── Carregamento ─────────────────────────────────────────────────────────────

/**
 * Uma promessa por carregamento de página, compartilhada por TODOS os
 * campos. Origem e Destino são dois componentes; sem esta memoização
 * seriam duas requisições para a mesma lista constante.
 */
let promessaBase = null;

/**
 * Carrega a base local uma única vez.
 *
 * Falha vira lista vazia — de propósito. Sem base local, `buscarLocal`
 * devolve [] e o componente cai no caminho remoto, que é exatamente o
 * comportamento que existia antes desta mudança. Degradar para "o de
 * antes" é aceitável; travar o campo não é.
 *
 * A falha também é memoizada: não adianta repetir a chamada a cada tecla
 * se ela acabou de falhar. Um reload resolve.
 */
export function carregarBaseLocal(apiUrl, fetchImpl) {
  if (promessaBase) return promessaBase;

  const buscar = fetchImpl || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
  if (!buscar) return Promise.resolve([]);

  promessaBase = buscar(`${apiUrl}${ROTA_BASE_LOCAL}`)
    .then((r) => (r && r.ok ? r.json() : { airports: [] }))
    .then((d) => (Array.isArray(d && d.airports) ? d.airports : []))
    .catch(() => []);

  return promessaBase;
}

/** Só para teste — zera a memoização entre casos. */
export function _limparBaseLocal() {
  promessaBase = null;
}
